import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import QuestionForm from './QuestionForm';

/**
 * Draggable question card component with edit and delete functionality.
 * 
 * Props:
 * - question: Question data object
 * - index: Position index (0-based)
 * - onEdit: Function called with (questionId, updatedData)
 * - onDelete: Function called with questionId
 */
function QuestionCard({ question, index, onEdit, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sortable hook for drag and drop
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  };

  const handleEdit = async (updatedData) => {
    await onEdit(question.id, updatedData);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setIsDeleting(true);
      try {
        await onDelete(question.id);
      } catch (err) {
        setIsDeleting(false);
      }
    }
  };

  if (isEditing) {
    return (
      <div ref={setNodeRef} style={style} className="question-card editing">
        <div className="question-card-header">
          <span className="question-number">Question {index + 1}</span>
        </div>
        <QuestionForm
          initialData={question}
          onSubmit={handleEdit}
          onCancel={() => setIsEditing(false)}
          submitLabel="Save Changes"
          isEditing={true}
        />
      </div>
    );
  }

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`question-card ${isDragging ? 'dragging' : ''}`}
    >
      <div className="question-card-header">
        <div className="drag-handle" {...attributes} {...listeners}>
          <span className="drag-icon">&#9776;</span>
        </div>
        <span className="question-number">Question {index + 1}</span>
        <div className="question-card-actions">
          <button 
            className="btn btn-edit" 
            onClick={() => setIsEditing(true)}
            title="Edit question"
          >
            Edit
          </button>
          <button 
            className="btn btn-delete" 
            onClick={handleDelete}
            disabled={isDeleting}
            title="Delete question"
          >
            {isDeleting ? '...' : 'Delete'}
          </button>
        </div>
      </div>
      
      <div className="question-text">{question.question}</div>
      
      <div className="question-options">
        <span className={question.correct_answer === 'A' ? 'correct-option' : ''}>
          <span className="option-indicator a">A</span> {question.option_a}
        </span>
        <span className={question.correct_answer === 'B' ? 'correct-option' : ''}>
          <span className="option-indicator b">B</span> {question.option_b}
        </span>
        <span className={question.correct_answer === 'C' ? 'correct-option' : ''}>
          <span className="option-indicator c">C</span> {question.option_c}
        </span>
        <span className={question.correct_answer === 'D' ? 'correct-option' : ''}>
          <span className="option-indicator d">D</span> {question.option_d}
        </span>
      </div>
    </div>
  );
}

export default QuestionCard;
